import { BriefcaseBusiness } from "lucide-react";
import {
  useDataProvider,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";

type QualifyResultStatus = "created" | "already-converted" | "failed";

type QualifyResult = {
  contactId: Identifier;
  projectId?: Identifier;
  status: QualifyResultStatus;
  message?: string;
};

type QualifyContactsResponse = {
  results: QualifyResult[];
};

type QualifyDataProvider = ReturnType<typeof useDataProvider> & {
  qualifyContacts?: (
    contactIds: Identifier[],
  ) => Promise<QualifyContactsResponse>;
};

export const BulkQualifyButton = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider() as QualifyDataProvider;
  const { onUnselectItems, selectedIds = [] } = useListContext();
  const [isQualifying, setIsQualifying] = useState(false);

  if (!selectedIds.length) return null;

  const qualify = async () => {
    if (!dataProvider.qualifyContacts) {
      notify("resources.contacts.qualification.error", { type: "error" });
      return;
    }

    setIsQualifying(true);

    try {
      const response = await dataProvider.qualifyContacts(selectedIds);
      const results = response?.results ?? [];
      const createdCount = results.filter(
        (result) => result.status === "created",
      ).length;
      const alreadyConvertedCount = results.filter(
        (result) => result.status === "already-converted",
      ).length;
      const failedCount = results.filter(
        (result) => result.status === "failed",
      ).length;

      if (createdCount > 0) {
        notify("resources.contacts.qualification.success", {
          type: "success",
          messageArgs: { smart_count: createdCount },
        });
      }

      if (alreadyConvertedCount > 0) {
        notify("resources.contacts.qualification.already_converted", {
          type: "info",
          messageArgs: { smart_count: alreadyConvertedCount },
        });
      }

      if (failedCount > 0 || results.length !== selectedIds.length) {
        notify("resources.contacts.qualification.error", {
          type: "warning",
        });
      }

      onUnselectItems();
      refresh();
    } catch (error) {
      notify("resources.contacts.qualification.error", { type: "error" });
      console.error("Bulk lead qualification failed:", error);
    } finally {
      setIsQualifying(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-9"
      disabled={isQualifying}
      onClick={qualify}
    >
      <BriefcaseBusiness />
      {translate("resources.contacts.qualification.action", {
        _: "Qualify",
      })}
    </Button>
  );
};
